import { memo } from 'react'
import Chart from 'react-apexcharts'

/**
 * Enhanced comparison function for SimpleChart memoization
 */
const areSimpleChartPropsEqual = (prevProps, nextProps) => {
  const shouldDebugLog = false // Set to true for debugging
    if (shouldDebugLog) {
    console.warn('SimpleChart arePropsEqual check:', {
      dataLength: { prev: prevProps.data?.length || 0, next: nextProps.data?.length || 0 },
      dataKey: prevProps.dataKey === nextProps.dataKey,
      color: prevProps.color === nextProps.color,
      height: prevProps.height === nextProps.height,
      showGrid: prevProps.showGrid === nextProps.showGrid,
      showAxes: prevProps.showAxes === nextProps.showAxes,
      targetAnnotation: prevProps.targetAnnotation === nextProps.targetAnnotation,
      unit: prevProps.unit === nextProps.unit,
    })
  }  // Compare primitive props first
  if (
    prevProps.dataKey !== nextProps.dataKey ||
    prevProps._color !== nextProps._color ||
    prevProps.height !== nextProps.height ||
    prevProps.showGrid !== nextProps.showGrid ||
    prevProps.showAxes !== nextProps.showAxes ||
    prevProps.targetAnnotation !== nextProps.targetAnnotation ||
    prevProps.measurement !== nextProps.measurement ||
    prevProps.unit !== nextProps.unit ||
    prevProps._dataSource !== nextProps._dataSource
  ) {
    if (shouldDebugLog) console.warn('SimpleChart PRIMITIVE PROPS CHANGED - re-rendering')
    return false
  }
  
  // Compare data arrays intelligently
  const prevData = prevProps.data || []
  const nextData = nextProps.data || []
    // If lengths are different, re-render
  if (prevData.length !== nextData.length) {
    if (shouldDebugLog) console.warn(`SimpleChart DATA LENGTH CHANGED (${prevData.length} -> ${nextData.length}) - re-rendering`)
    return false
  }
  
  // If both empty, no re-render needed
  if (prevData.length === 0) {
    if (shouldDebugLog) console.warn('SimpleChart BOTH DATA EMPTY - skipping render')
    return true
  }
  
  // Compare last few data points for meaningful changes
  const compareCount = Math.min(2, prevData.length)
  for (let i = 0; i < compareCount; i++) {
    const prevIndex = prevData.length - 1 - i
    const nextIndex = nextData.length - 1 - i
    
    const prevPoint = prevData[prevIndex]
    const nextPoint = nextData[nextIndex]
    
    if (!prevPoint || !nextPoint) continue
    
    // Compare the data values that matter for the chart
    const prevValue = prevPoint[nextProps.dataKey] || prevPoint.y || prevPoint.value
    const nextValue = nextPoint[nextProps.dataKey] || nextPoint.y || nextPoint.value
    const prevTime = prevPoint.timestamp || prevPoint.x
    const nextTime = nextPoint.timestamp || nextPoint.x
      if (prevValue !== nextValue || prevTime !== nextTime) {
      return false
    }
  }
  
  return true // Props are functionally equal
}

/**
 * SimpleChart - A basic chart component for displaying metric data
 * Used primarily in MetricCard components for inline data visualization
 * Enhanced to support target line annotations with different colors based on source
 * Enhanced to support CAN bus source indicators in chart subtitles
 */
function SimpleChart({ 
  data = [], 
  dataKey = 'value', 
  _color = '#3b82f6',  // Legacy color prop, now replaced by fault-aware coloring
  height = 60,
  showGrid = false,
  showAxes = false,
  targetAnnotation = null,
  measurement = null,
  _hasFault = false,  // Legacy fault prop (no longer used for coloring)
  unit = '',
  _dataSource = null,  // CAN bus source indicator (used for memoization)
}) {
  // If no data, show empty state
  if (!data || data.length === 0) {
    return (
      <div className="simple-chart-empty" style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="text-muted small">No data</span>
      </div>
    )
  }  // For SOC measurements, render as custom semi-circle gauge
  if (measurement === 'soc') {
    // Get the latest SOC value from the data
    const latestValue = data[data.length - 1]
    const socValue = latestValue ? (latestValue[dataKey] || latestValue.y || latestValue.value) : 0
      // Determine color based on SOC level only
    // Red for low battery (< 30%), green for normal levels
    let chartColor
    if (socValue < 30) {
      chartColor = '#dc3545' // Red for low battery
    } else {
      chartColor = '#28a745' // Green for normal operation
    }
    
    // Custom gauge options optimized for SOC display in metric cards
    const gaugeOptions = {chart: {
        type: 'radialBar',
        height,
        toolbar: { show: false },
        sparkline: { enabled: true },
      },
      plotOptions: {        radialBar: {
          startAngle: -135,  // Start at upper left
          endAngle: 135,     // End at upper right (270° arc)
          hollow: {
            margin: 2,
            size: '65%',
            background: 'transparent',
          },
          track: {
            background: '#e9ecef',
            strokeWidth: '100%',
            margin: 2,
          },
          dataLabels: {
            show: false,  // Hide all labels to avoid duplication with main value display
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'horizontal',
          shadeIntensity: 0.25,          gradientToColors: [chartColor],
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },      colors: [chartColor],
      stroke: {
        lineCap: 'round'      },
      tooltip: {
        enabled: false,  // Disable tooltip for SOC gauge
      },
    }

    const series = [socValue]

    return (
      <Chart 
        options={gaugeOptions} 
        series={series} 
        type="radialBar" 
        height={height} 
      />
    )
  }// Transform data for ApexCharts
  const chartData = data.map(item => ({
    x: item.timestamp || item.x,
    y: item[dataKey] || item.y || item.value,
  }))
  // Build annotations if target annotation is provided
  let annotations = {}
  let yAxisRange = {}
  
  // Apply smart Y-axis range calculation (matching HTML dashboard logic)
  const dataValues = chartData.map(item => item.y).filter(y => y !== null && y !== undefined)
  if (dataValues.length > 0) {
    const yMinValue = Math.min(...dataValues)
    const yMaxValue = Math.max(...dataValues)
    
    // Apply the same logic as HTML dashboard: Math.max(3, value * 0.15)
    const yMin = yMinValue - Math.max(3, Math.abs(yMinValue) * 0.15)
    const yMax = yMaxValue + Math.max(3, Math.abs(yMaxValue) * 0.15)
    
    yAxisRange = {
      min: yMin,
      max: yMax,
    }
  }
  
  if (targetAnnotation) {
    annotations = {
      yaxis: targetAnnotation.yaxis,
    }
    
    // Adjust y-axis range if target annotation provides it
    if (targetAnnotation.yAxisAdjustment) {
      const targetMin = targetAnnotation.yAxisAdjustment.min
      const targetMax = targetAnnotation.yAxisAdjustment.max
      
      // Merge with existing smart range, taking the broader range
      yAxisRange = {
        min: Math.min(yAxisRange.min || targetMin, targetMin),
        max: Math.max(yAxisRange.max || targetMax, targetMax),
      }
    }
  }  const chartColor = '#28a745' // Always green for normal operation

  // Build chart subtitle - keep empty for metric cards to avoid redundancy
  const getChartSubtitle = () => {
    // Return empty string since measurement name is already shown in card header
    return ''
  }

  const options = {
    chart: {
      type: 'line',
      height,
      sparkline: {
        enabled: !showAxes,
      },
      toolbar: {
        show: false,
      },
      parentHeightOffset: 0,
      offsetY: 0,
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    colors: [chartColor],
    title: {
      text: '', // Keep title empty for metric cards
      style: {
        fontSize: '14px',
        color: '#495057',
      },
    },
    subtitle: {
      text: getChartSubtitle(),
      align: 'center',
      style: {
        fontSize: '11px',
        color: '#6c757d',
        fontWeight: 'normal',
      },
    },
    grid: {
      show: showGrid,
      padding: {
        top: 5,
        right: 5, // Minimal right padding - use all horizontal space
        bottom: 5,
        left: 5,
      },
    },
    xaxis: {
      show: showAxes,
      type: 'datetime',
    },
    yaxis: {
      show: showAxes,
      ...yAxisRange,
    },    tooltip: {
      enabled: true,
      theme: 'light',
      shared: false,
      intersect: false,
      style: {
        fontSize: '12px',
        fontFamily: 'inherit',
      },
      custom({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex][dataPointIndex]
        const timestamp = w.globals.seriesX[seriesIndex][dataPointIndex]
        const date = new Date(timestamp)
        
        return `
          <div style="
            background: rgba(255, 255, 255, 0.95);
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 8px 12px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            backdrop-filter: blur(10px);
            font-size: 12px;
            line-height: 1.4;
          ">
            <div style="font-weight: 600; color: #495057; margin-bottom: 4px;">
              ${measurement || 'Value'}
            </div>
            <div style="font-weight: 700; color: #007bff; font-size: 14px;">
              ${value !== null && value !== undefined ? (unit ? `${value}${unit}` : value) : 'N/A'}
            </div>
            <div style="color: #6c757d; font-size: 11px; margin-top: 4px;">
              ${date.toLocaleTimeString()}
            </div>
          </div>
        `
      },
    },
    annotations,
  };const series = [{
    name: dataKey,
    data: chartData  }]
  
  return (
    <Chart 
      options={options} 
      series={series} 
      type="line" 
      height={height} 
    />
  )
}

export default memo(SimpleChart, areSimpleChartPropsEqual)
